// Family-level alerts (pure). See plan §5.5.
//   variant_conflict     — lab vs ClinVar disagree on a genotype
//   consanguinity        — a consanguineous union (elevates AR prior)
//   data_gap             — affected individuals missing age of onset
//   reduced_penetrance   — apparent skipped generation (unaffected obligate transmitter)

import { ALERT_KIND } from '../constants';
import { affectedList, isAffectedBy } from './inheritance.helpers';

function alert(id, kind, severity, affectedIds, detail) {
  return { id, kind, severity, affectedIds, detail };
}

export function detectAlerts(ped) {
  const out = [];

  for (const ind of ped.individuals) {
    for (const g of ind.genotypes) {
      if (g.clinvarConflict) {
        out.push(
          alert(
            `variant_conflict:${ind.pid}:${g.gene}`,
            ALERT_KIND.variantConflict,
            'review',
            [ind.pid],
            `Laboratory and ClinVar classifications differ for ${g.gene} — see the record for each source`
          )
        );
      }
    }
  }

  for (const loop of ped.consanguinityLoops || []) {
    out.push(
      alert(
        `consanguinity:${loop.partners.join('|')}`,
        ALERT_KIND.consanguinity,
        'info',
        loop.partners,
        'Consanguineous union — elevates recessive risk'
      )
    );
  }

  for (const disease of ped.familyDiseases) {
    const affected = affectedList(ped, disease.id);
    const missingAge = affected.filter((i) => i.ageAtAssessment == null);
    if (affected.length && missingAge.length) {
      out.push(
        alert(
          `data_gap:age:${disease.id}`,
          ALERT_KIND.dataGap,
          'info',
          missingAge.map((i) => i.pid),
          `Missing age of onset for affected individual(s) of ${
            disease.name || disease.id
          }`
        )
      );
    }

    // Reduced penetrance: an unaffected individual with both an affected parent
    // and an affected child (obligate transmitter who does not manifest).
    for (const ind of ped.individuals) {
      if (isAffectedBy(ind, disease.id)) continue;
      const parentAffected = [ind.fatherId, ind.motherId].some((id) => {
        const p = id && ped.byPid.get(id);
        return p && isAffectedBy(p, disease.id);
      });
      const childAffected = ind.childrenIds.some((id) =>
        isAffectedBy(ped.byPid.get(id), disease.id)
      );
      if (parentAffected && childAffected) {
        out.push(
          alert(
            `reduced_penetrance:${disease.id}:${ind.pid}`,
            ALERT_KIND.reducedPenetrance,
            'info',
            [ind.pid],
            `Apparent skipped generation for ${
              disease.name || disease.id
            } (possible reduced penetrance)`
          )
        );
      }
    }
  }

  return out;
}
